/**
 * Goals + Claims CSV / JSON import + export helpers.
 *
 * Each collection has its own pair of export / import functions
 * since they live in separate columns and have different ID fields.
 * Lists (`risk_ids`) round-trip via `; ` joining inside quoted cells.
 */
import { GOAL_COLUMNS_READ, type GoalRecord } from './goalsFieldSchema';
import { CLAIM_COLUMNS_READ, type ClaimRecord } from './claimsFieldSchema';

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

function buildCsv(columns: { key: string; label: string }[], rows: Record<string, unknown>[]): string {
  const header = columns.map((c) => c.label).join(',');
  const body = rows.map((r) =>
    columns.map((c) => escapeCsv(r[c.key])).join(','),
  );
  return [header, ...body].join('\n');
}

function triggerDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function exportGoalsToCsv(goals: GoalRecord[]): void {
  const csv = buildCsv(GOAL_COLUMNS_READ, goals as Record<string, unknown>[]);
  triggerDownload(csv, `cybersecurity_goals_${new Date().toISOString().split('T')[0]}.csv`);
}

export function exportClaimsToCsv(claims: ClaimRecord[]): void {
  const csv = buildCsv(CLAIM_COLUMNS_READ, claims as Record<string, unknown>[]);
  triggerDownload(csv, `cybersecurity_claims_${new Date().toISOString().split('T')[0]}.csv`);
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

const GOAL_HEADER_OVERRIDES: Record<string, string> = {
  id: 'goal_id',
  goal_statement: 'goal_statement',
  linked_risks: 'risk_ids',
  property: 'security_property',
  cal: 'cal_level',
};

const CLAIM_HEADER_OVERRIDES: Record<string, string> = {
  id: 'claim_id',
  claim_statement: 'claim_statement',
  justification: 'justification',
  linked_risks: 'risk_ids',
};

function headerToKey(
  header: string,
  columns: { key: string; label: string }[],
  overrides: Record<string, string>,
): string {
  const col = columns.find((c) => c.label.toLowerCase() === header.trim().toLowerCase());
  if (col) return col.key;
  const slug = header.trim().toLowerCase().replace(/\s+/g, '_');
  return overrides[slug] ?? slug;
}

const GOAL_LIST_KEYS = new Set(['risk_ids']);
const CLAIM_LIST_KEYS = new Set(['risk_ids']);

function parseCsvWithSchema<T>(
  text: string,
  columns: { key: string; label: string }[],
  overrides: Record<string, string>,
  listKeys: Set<string>,
): T[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => headerToKey(h, columns, overrides));
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => {
      const raw = row[i] ?? '';
      out[key] = listKeys.has(key)
        ? raw
            .split(/[;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : raw;
    });
    return out as T;
  });
}

export function parseGoalsCsv(text: string): GoalRecord[] {
  return parseCsvWithSchema<GoalRecord>(text, GOAL_COLUMNS_READ, GOAL_HEADER_OVERRIDES, GOAL_LIST_KEYS);
}

export function parseClaimsCsv(text: string): ClaimRecord[] {
  return parseCsvWithSchema<ClaimRecord>(text, CLAIM_COLUMNS_READ, CLAIM_HEADER_OVERRIDES, CLAIM_LIST_KEYS);
}

export function parseGoalsJson(text: string): GoalRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as GoalRecord[];
  if (parsed && Array.isArray(parsed.cybersecurity_goals)) return parsed.cybersecurity_goals as GoalRecord[];
  if (parsed && Array.isArray(parsed.goals)) return parsed.goals as GoalRecord[];
  throw new Error('JSON must be an array of goals or `{ cybersecurity_goals: [...] }`');
}

export function parseClaimsJson(text: string): ClaimRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as ClaimRecord[];
  if (parsed && Array.isArray(parsed.cybersecurity_claims)) return parsed.cybersecurity_claims as ClaimRecord[];
  if (parsed && Array.isArray(parsed.claims)) return parsed.claims as ClaimRecord[];
  throw new Error('JSON must be an array of claims or `{ cybersecurity_claims: [...] }`');
}

export async function parseGoalsFile(file: File): Promise<GoalRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseGoalsJson(text);
  if (ext === 'csv') return parseGoalsCsv(text);
  try { return parseGoalsJson(text); } catch { return parseGoalsCsv(text); }
}

export async function parseClaimsFile(file: File): Promise<ClaimRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseClaimsJson(text);
  if (ext === 'csv') return parseClaimsCsv(text);
  try { return parseClaimsJson(text); } catch { return parseClaimsCsv(text); }
}
